// ABOUTME: Cross-tab safety for the persisted AI chat store (#2208). Two tabs
// share the single `ai-chat-storage` localStorage key, and zustand persist
// writes the whole conversations array on every change — so whichever tab
// wrote last used to replace the entire array, permanently deleting every
// conversation the other tab held. This module owns the union-by-id merge and
// the merge-then-write storage wrapper; useAIChatStore wires them up and adds
// the `storage` event listener that makes live state converge.

import type { PersistStorage, StorageValue } from 'zustand/middleware'
import type { AIConversation } from '@/stores/aiChatTypes'

/** Persisted slice of the chat store — what `partialize` emits. */
export interface PersistedAIChatState {
  conversations: AIConversation[]
  activeConversationId: string | null
  /**
   * Tombstones for deliberate deletes. Without them a union-by-id merge cannot
   * tell "the other tab never had this conversation" from "the other tab just
   * deleted it", so every delete would be resurrected by the next write from
   * any other open tab — after the backend session was already erased.
   */
  deletedConversationIds: string[]
}

/** Bump alongside the persist `version` option in useAIChatStore. */
export const CHAT_STORE_VERSION = 2

/** Tombstones are only needed while another tab might still hold the row, so a
 *  small rolling window is plenty; the cap stops the blob growing forever. */
const MAX_DELETED_IDS = 200

/** #1923 defence-in-depth — every merge path must drop foreign-tenant rows. A
 *  null active business means auth has not hydrated and nothing can be judged. */
export function filterToBusiness(
  conversations: readonly AIConversation[],
  activeBusinessId: string | null
): AIConversation[] {
  return conversations.filter((c) => !activeBusinessId || c.businessId === activeBusinessId)
}

/**
 * A message restored from storage can never still be streaming — streams die
 * with the page that owned them. A tab closed (or reloaded) mid-stream
 * persists its placeholder with `isStreaming: true`, and one such flag used to
 * permanently abort every resume watch on the conversation, which is how a
 * whole run of resumed replies never reached the tab waiting for them (#2278).
 *
 * The same reasoning applies verbatim to a SEND that died with its page
 * (#2281): a user message still `status: 'pending'` on the way in has no
 * in-flight request behind it — `sendOrQueue`'s queue, and the request
 * itself, both live only in memory and die on reload — so it would otherwise
 * rehydrate as a permanent, unrecoverable "Sending…" with no retry control.
 * Flip it to `failed` here, the same spot that already owns "reconcile
 * state a dead page left behind."
 *
 * Clears/flips on the way in; returns the input array unchanged (same
 * reference) when nothing needs fixing.
 */
export function finalizeInterruptedStreams(conversations: AIConversation[]): AIConversation[] {
  let changed = false
  const result = conversations.map((conversation) => {
    const needsFinalizing = conversation.messages.some(
      (m) => m.isStreaming || (m.role === 'user' && m.status === 'pending')
    )
    if (!needsFinalizing) return conversation
    changed = true
    return {
      ...conversation,
      messages: conversation.messages.map((m) => {
        if (m.isStreaming) return { ...m, isStreaming: false }
        if (m.role === 'user' && m.status === 'pending') return { ...m, status: 'failed' as const }
        return m
      }),
    }
  })
  return changed ? result : conversations
}

/**
 * Conflict rule for the same id present in both copies: the copy with MORE
 * MESSAGES wins; on a tie, the later `updatedAt` wins. Message count is the
 * primary key because the bug being fixed is silent truncation — in the #2208
 * incident the losing copy was a 3-message rewrite of a 7-message conversation,
 * and the rewrite carried the newer timestamp.
 */
function pickRicherCopy(mine: AIConversation, theirs: AIConversation): AIConversation {
  if (mine.messages.length !== theirs.messages.length) {
    return mine.messages.length > theirs.messages.length ? mine : theirs
  }
  return theirs.updatedAt > mine.updatedAt ? theirs : mine
}

/**
 * Union-by-id merge of two conversation lists. Local relative order is kept;
 * conversations only the other copy has are prepended in that copy's order
 * (matching where the tab that created them shows them). Returns the `local`
 * array UNCHANGED (same reference) when the merge is a no-op — the `storage`
 * event handler uses that identity to stop write→event→write ping-pong
 * between tabs.
 */
export function mergeConversationLists(
  local: AIConversation[],
  incoming: readonly AIConversation[],
  deletedIds: ReadonlySet<string>
): AIConversation[] {
  const incomingById = new Map(incoming.map((c) => [c.id, c]))
  const localIds = new Set(local.map((c) => c.id))

  let changed = false
  const kept: AIConversation[] = []
  for (const mine of local) {
    if (deletedIds.has(mine.id)) {
      changed = true
      continue
    }
    const theirs = incomingById.get(mine.id)
    const winner = theirs ? pickRicherCopy(mine, theirs) : mine
    if (winner !== mine) changed = true
    kept.push(winner)
  }

  const incomingOnly = incoming.filter((c) => !localIds.has(c.id) && !deletedIds.has(c.id))
  if (incomingOnly.length > 0) changed = true

  return changed ? [...incomingOnly, ...kept] : local
}

/** Union two tombstone lists, capped. Returns `mine` unchanged when `theirs`
 *  adds nothing, so callers can use identity as a no-change signal. */
export function unionDeletedIds(mine: string[], theirs: readonly string[]): string[] {
  const known = new Set(mine)
  const fresh = theirs.filter((id) => !known.has(id))
  if (fresh.length === 0) return mine
  return [...mine, ...fresh].slice(-MAX_DELETED_IDS)
}

/** Append one tombstone, capped. */
export function appendDeletedId(ids: string[], id: string): string[] {
  return [...ids.filter((existing) => existing !== id), id].slice(-MAX_DELETED_IDS)
}

/**
 * Compute the state delta a `storage` event should apply, or `null` for a
 * no-op — kept store-agnostic so useAIChatStore's listener stays a thin
 * getState/setState wrapper instead of carrying this whole merge inline.
 * `activeConversationId` is only present on the return value when the
 * writing tab's change orphaned this tab's active conversation, mirroring
 * what any other conversation-lifecycle boundary does to ephemeral state.
 */
export function computeCrossTabMerge(
  current: {
    conversations: AIConversation[]
    deletedConversationIds: string[]
    activeConversationId: string | null
  },
  stored: StorageValue<PersistedAIChatState> | null,
  activeBusinessId: string | null
): {
  conversations: AIConversation[]
  deletedConversationIds: string[]
  activeConversationId?: string | null
} | null {
  // Only the current shape merges; a v1 blob is the #1923 unstamped leak.
  if (!stored?.state || stored.version !== CHAT_STORE_VERSION) return null

  const deletedConversationIds = unionDeletedIds(
    current.deletedConversationIds,
    stored.state.deletedConversationIds ?? []
  )
  const incoming = filterToBusiness(stored.state.conversations ?? [], activeBusinessId)
  const conversations = mergeConversationLists(
    current.conversations,
    incoming,
    new Set(deletedConversationIds)
  )
  // Identity means the merge was a no-op — the caller must skip setState, or
  // the resulting persist write fires a storage event back at the writing
  // tab and the two tabs ping-pong forever.
  if (
    conversations === current.conversations &&
    deletedConversationIds === current.deletedConversationIds
  ) {
    return null
  }

  const activeGone =
    current.activeConversationId !== null &&
    !conversations.some((c) => c.id === current.activeConversationId)

  return {
    conversations,
    deletedConversationIds,
    ...(activeGone ? { activeConversationId: conversations[0]?.id ?? null } : {}),
  }
}

/** Parse a raw `ai-chat-storage` blob. Returns null on garbage; makes no
 *  version judgement — rehydrate needs to see old versions to `migrate` them,
 *  while the merge paths check `version` themselves and skip anything that is
 *  not the current shape. */
export function parseChatBlob(raw: string | null): StorageValue<PersistedAIChatState> | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StorageValue<PersistedAIChatState>
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

/**
 * Merge-then-write storage for the chat store. `setItem` reads the value some
 * other tab may have written since this tab last looked and unions it in by
 * conversation id, so a tab writing its own array can never drop ids it never
 * had. Only a blob of the CURRENT version is merged — a v1 blob is the #1923
 * unstamped leak and must stay on its discard-via-migrate path, never merge.
 */
export function createCrossTabChatStorage(
  getActiveBusinessId: () => string | null
): PersistStorage<PersistedAIChatState> {
  return {
    getItem: (name) => parseChatBlob(localStorage.getItem(name)),
    setItem: (name, value) => {
      const stored = parseChatBlob(localStorage.getItem(name))
      let state = value.state
      if (stored?.state && stored.version === CHAT_STORE_VERSION) {
        const deletedConversationIds = unionDeletedIds(
          value.state.deletedConversationIds ?? [],
          stored.state.deletedConversationIds ?? []
        )
        const fromStorage = filterToBusiness(
          stored.state.conversations ?? [],
          getActiveBusinessId()
        )
        state = {
          ...value.state,
          conversations: mergeConversationLists(
            value.state.conversations,
            fromStorage,
            new Set(deletedConversationIds)
          ),
          deletedConversationIds,
        }
      }
      localStorage.setItem(name, JSON.stringify({ version: value.version, state }))
    },
    removeItem: (name) => localStorage.removeItem(name),
  }
}
